import { expireSession, getIdToken } from '$features/auth/stores/auth';
import { getConfig, getConfigOrNull, loadConfig } from '$config';
import * as Sentry from '@sentry/sveltekit';

/**
 * API Client Configuration
 */
export interface ApiClientConfig {
  baseUrl: string;
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  /** When false, do not retry failed requests (useful for long-running creates). */
  retry?: boolean;
}

// Repeating these can't apply a change twice, so a failed attempt may be retried automatically.
const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'PUT', 'DELETE']);

const STATUS_MESSAGES: Record<number, string> = {
  400: 'We couldn’t complete that. Check the details and try again.',
  401: 'Your session has ended. Sign in again to continue.',
  403: 'You don’t have access to this.',
  404: 'We couldn’t find that. It may have been moved or deleted.',
  408: 'STAT took too long to respond. Try again.',
  409: 'This has changed since you opened it. Refresh the page and try again.',
  413: 'That file is too large. Choose a smaller file and try again.',
  422: 'We couldn’t complete that. Check the details and try again.',
  429: 'Too many attempts in a short time. Wait a minute and try again.',
  503: 'STAT is temporarily unavailable. Try again in a moment.',
  504: 'STAT took too long to respond. Try again in a moment.',
};

/** The words a person sees for a failed request when the API sends no message of its own. */
export function statusMessage(status: number): string {
  if (STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];
  if (status >= 500) return 'Something went wrong on our side. Try again in a moment.';
  return 'Something went wrong. Try again.';
}

// API Gateway and framework defaults that only restate the status; the status message says it better.
const GENERIC_MESSAGES =
  /^(bad request|unauthori[sz]ed|forbidden|not found|internal server error|bad gateway|service unavailable|gateway time-?out|endpoint request timed out|missing authentication token|one or more validation errors occurred\.?|an error occurred while processing your request\.?|user is not authori[sz]ed to access this resource.*)$/i;

function firstText(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(firstText).find(Boolean);
  if (value && typeof value === 'object') return Object.values(value).map(firstText).find(Boolean);
  return undefined;
}

/** The API's own sentence for a failure (`message`, `error`, a validation error or plain text), when it has a usable one. */
export function readServerMessage(body: unknown): string | null {
  let message: string | undefined;
  if (typeof body === 'string') {
    message = body;
  } else if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    message =
      firstText(record.message ?? record.Message)?.trim() ||
      firstText(record.error ?? record.Error)?.trim() ||
      firstText(record.errors ?? record.Errors)?.trim() ||
      firstText(record.detail ?? record.title);
  }
  const text = message?.trim();
  // Long bodies are stack traces or HTML error pages, never something to show.
  if (!text || text.length > 300 || /<[a-z!/]/i.test(text) || GENERIC_MESSAGES.test(text)) return null;
  return text;
}

/** Its message is the API's own sentence, else plain words for the status, so pages can show it as it is. */
export class ApiError extends Error {
  /** The API's own message, or null when `message` is the fallback for the status. */
  readonly serverMessage: string | null;

  constructor(
    public status: number,
    public statusText: string,
    public body?: unknown,
    message?: string
  ) {
    super(message || readServerMessage(body) || statusMessage(status));
    this.name = 'ApiError';
    this.serverMessage = readServerMessage(body);
  }

  /** The API's machine-readable code (e.g. INSUFFICIENT_ORGANISATION_CREDIT), when it sent one. */
  get code(): string | undefined {
    if (!this.body || typeof this.body !== 'object') return undefined;
    const record = this.body as Record<string, unknown>;
    const code = record.code ?? record.Code;
    return typeof code === 'string' && code ? code : undefined;
  }
}

/** No response arrived: the connection dropped or the browser blocked the request. */
export class NetworkError extends Error {
  constructor() {
    super('We couldn’t reach STAT. Check your connection and try again.');
    this.name = 'NetworkError';
  }
}

/** The request ran past its timeout, so the API may still finish it. */
export class RequestTimeoutError extends Error {
  constructor() {
    super(statusMessage(408));
    this.name = 'TimeoutError';
  }
}

/** A failure that may still have taken effect (no response, a timeout or a 5xx): reload before allowing a re-send. */
export function isUncertainOutcome(error: unknown): boolean {
  return !(error instanceof ApiError) || error.status >= 500;
}

/** One per money action, sent as `idempotencyKey` in the body (a header would fail CORS preflight); reuse it only to re-send that action. */
export function createIdempotencyKey(): string {
  return crypto.randomUUID();
}

export interface RequestOptions {
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  skipAuth?: boolean;
  timeout?: number;
  /** Only GET, PUT and DELETE retry by default; true opts in a read-only POST, false turns retries off. */
  retry?: boolean;
  /** When false, omit JSON Content-Type (e.g. multipart FormData). */
  jsonBody?: boolean;
}

/**
 * Create an API client with automatic auth header injection and error handling
 */
export function createApiClient(config?: Partial<ApiClientConfig>) {
  const defaultConfig: ApiClientConfig = {
    baseUrl: '',
    timeout: 30000,
    retries: 3,
    retryDelay: 1000,
    ...config,
  };

  /**
   * Get the base URL, loading config if necessary
   */
  async function getBaseUrl(): Promise<string> {
    if (defaultConfig.baseUrl) {
      return defaultConfig.baseUrl;
    }

    // Try to get config, load if not available
    let appConfig = getConfigOrNull();
    if (!appConfig) {
      await loadConfig();
      appConfig = getConfig();
    }

    return appConfig.api.host;
  }

  /**
   * Make an API request with retry logic and auth
   */
  async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
    const { body, headers = {}, signal, skipAuth = false, jsonBody = true } = options;
    const requestTimeout = options.timeout ?? defaultConfig.timeout;
    // A repeated POST could charge or credit twice, so only idempotent methods retry unless the caller says otherwise.
    const retryable = options.retry ?? (defaultConfig.retry !== false && IDEMPOTENT_METHODS.has(method));
    const requestRetries = retryable ? defaultConfig.retries || 1 : 1;

    // Get base URL (loads config if needed)
    const baseUrl = await getBaseUrl();

    // Build URL
    const url = `${baseUrl}${path}`;

    // Build headers
    const requestHeaders: Record<string, string> = { ...headers };
    if (jsonBody) {
      requestHeaders['Content-Type'] = 'application/json';
    }

    // Build request options
    const requestOptions: RequestInit = {
      method,
      headers: requestHeaders,
      signal,
    };

    if (body) {
      requestOptions.body = jsonBody ? JSON.stringify(body) : (body as BodyInit);
    }

    const wait = () => new Promise((resolve) => setTimeout(resolve, defaultConfig.retryDelay));

    // Retry logic
    let lastError: Error | null = null;
    for (let attempt = 0; attempt < requestRetries; attempt++) {
      try {
        // Read per attempt: a retry can start after the first attempt's token expired.
        if (!skipAuth) {
          const token = await getIdToken();
          if (token) requestHeaders['Authorization'] = `Bearer ${token}`;
        }

        // Add timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

        const finalSignal = signal || controller.signal;

        let response: Response;
        try {
          response = await fetch(url, {
            ...requestOptions,
            signal: finalSignal,
          });
        } finally {
          clearTimeout(timeoutId);
        }

        // Handle non-OK responses
        if (!response.ok) {
          // Read response body as text first to avoid "body stream already read" error
          const responseText = await response.text();
          // Try to parse as JSON, fall back to text if parsing fails
          let errorBody;
          try {
            errorBody = JSON.parse(responseText);
          } catch {
            errorBody = responseText;
          }

          const error = new ApiError(response.status, response.statusText, errorBody);

          // Handle 401 Unauthorized - token expired or invalid
          if (response.status === 401) {
            await expireSession();
            throw error;
          }

          // Don't retry client errors (4xx) except 429 (rate limit)
          if (response.status >= 400 && response.status < 500 && response.status !== 429) {
            Sentry.captureException(error, {
              extra: {
                url,
                method,
                status: response.status,
                body: errorBody,
              },
            });
            throw error;
          }

          // Retry server errors (5xx) and rate limits
          lastError = error;
          if (attempt < requestRetries - 1) {
            await wait();
            continue;
          }

          Sentry.captureException(error, {
            extra: {
              url,
              method,
              status: response.status,
              body: errorBody,
              attempt,
            },
          });
          throw error;
        }

        // Parse response
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          return (await response.json()) as T;
        }

        // Return empty object for no content
        if (response.status === 204) {
          return {} as T;
        }

        // Return text as-is
        return (await response.text()) as T;
      } catch (error) {
        // Re-throw ApiError
        if (error instanceof ApiError) {
          throw error;
        }

        // The caller cancelled the request: not a failure to report or retry.
        if (signal?.aborted) {
          throw error;
        }

        // Handle abort/timeout
        if (error instanceof Error && error.name === 'AbortError') {
          const timeoutError = new RequestTimeoutError();
          Sentry.captureException(timeoutError, {
            extra: { url, method, timeout: requestTimeout },
          });
          throw timeoutError;
        }

        // fetch rejects with a TypeError only when no response arrived ("Failed to fetch", "Load failed", …).
        if (error instanceof TypeError) {
          lastError = new NetworkError();
          if (attempt < requestRetries - 1) {
            await wait();
            continue;
          }
          Sentry.captureException(lastError, {
            extra: { url, method, attempt },
          });
          throw lastError;
        }

        // Unknown error
        lastError = error as Error;
        if (attempt < requestRetries - 1) {
          await wait();
          continue;
        }

        Sentry.captureException(error, {
          extra: { url, method, attempt },
        });
        throw error;
      }
    }

    // If we get here, all retries failed
    throw lastError || new Error('Request failed after retries');
  }

  type Options = Omit<RequestOptions, 'body'>;

  return {
    get: <T>(path: string, options?: Options) => request<T>('GET', path, options),

    post: <T>(path: string, body?: unknown, options?: Options) => request<T>('POST', path, { ...options, body }),

    postForm: <T>(path: string, formData: FormData, options?: Options) =>
      request<T>('POST', path, { ...options, body: formData, jsonBody: false }),

    put: <T>(path: string, body?: unknown, options?: Options) => request<T>('PUT', path, { ...options, body }),

    putForm: <T>(path: string, formData: FormData, options?: Options) =>
      request<T>('PUT', path, { ...options, body: formData, jsonBody: false }),

    patch: <T>(path: string, body?: unknown, options?: Options) => request<T>('PATCH', path, { ...options, body }),

    /** DELETE with an optional JSON body — some API endpoints (e.g. messaging) read their arguments from the body. */
    delete: <T>(path: string, body?: unknown, options?: Options) => request<T>('DELETE', path, { ...options, body }),
  };
}

/**
 * Default API client instance
 */
export const api = createApiClient();
